<script lang="ts">
    import { cn } from '$lib/utils/cn';

    import { nanoid } from 'nanoid/non-secure';

    import Checkbox from './Checkbox/Checkbox.svelte';
    import Text from '../../Text/Text.View.svelte';
    import { resolveInputStyling } from '../_utils/resolveInputStyling';

    import type { BrandBorderRadius, Theme } from '$lib/types/Theme';

    export let theme: Theme | undefined = undefined;
    export let invalid = false;
    export let wysiwygHtml: string | undefined = undefined;
    export let finalFormInput: {
        onFocus: () => void;
        onBlur: () => void;
        onChange: (value: boolean) => void;
        value?: unknown;
    } = {
        onFocus: () => {},
        onBlur: () => {},
        onChange: () => {},
        value: undefined,
    };
    export let errorMessage = '';

    let checkboxElementContainer: HTMLDivElement | undefined;

    $: {
        // this will detect when the focusable checkbox has been focused or blurred
        // like this we can use final form input's way of detecting if the input is touched
        if (checkboxElementContainer) {
            const checkboxElements = checkboxElementContainer.getElementsByTagName('input');

            if (checkboxElements.length === 1) {
                checkboxElements[0].addEventListener('focus', finalFormInput.onFocus);
                checkboxElements[0].addEventListener('blur', finalFormInput.onBlur);
            }
        }
    }

    const handleClick = ({ detail }: CustomEvent<boolean>) => {
        finalFormInput.onFocus();
        finalFormInput.onChange(detail);
    };

    const checkboxId = `checkbox-${nanoid()}`;

    const { borderRadiusClass, shadowClass, borderColor, backgroundColor, mutedTextColor } =
        resolveInputStyling({
            theme,
            shadowComponent: 'checkboxInput',
        });

    const isThemeV2 = !!backgroundColor;

    const CHECKBOX_RX: Record<BrandBorderRadius, string> = {
        none: '0%',
        sm: '35%',
        md: '50%',
        lg: '50%',
        xl: '50%',
        '2xl': '50%',
        full: '50%',
    };

    const checkboxRx =
        isThemeV2 && theme?.borderRadius
            ? CHECKBOX_RX[theme.borderRadius as BrandBorderRadius] ?? '15%'
            : '15%';

    const wrapperClasses = cn(
        'fun-relative fun-box-border fun-flex fun-items-center fun-py-2 fun-transition-all',
        borderRadiusClass,
    );

    const checkboxBoxClasses = cn(
        'fun-absolute fun-left-px fun-top-px fun-bg-white',
        isThemeV2 ? borderRadiusClass : 'fun-rounded',
        isThemeV2 ? 'fun-size-[18px]' : 'fun-size-[22px]',
        shadowClass,
    );

    const containerClasses = cn(
        'fun-relative',
        isThemeV2 ? 'fun-min-h-5 fun-min-w-5' : 'fun-min-h-6 fun-min-w-6',
    );
</script>

<div class={wrapperClasses} bind:this={checkboxElementContainer}>
    <div class={containerClasses}>
        <div
            class={checkboxBoxClasses}
            style:border-color={borderColor}
            style:background-color={backgroundColor}
        />

        <Checkbox
            size={isThemeV2 ? '20px' : '24px'}
            primaryColor="#0BB648"
            secondaryColor={borderColor ?? '#E5E7EB'}
            checked={!!finalFormInput.value}
            on:change={handleClick}
            class="fun-absolute fun-inset-0 fun-flex-shrink-0"
            labelId={checkboxId}
            rx={checkboxRx}
        />
    </div>

    <div class="fun-flex-col">
        <label for={checkboxId}>
            <Text
                {wysiwygHtml}
                align="left"
                color={mutedTextColor ?? theme?.fontColor ?? '#0F0F0F'}
                box={{ top: 'none', left: 'medium', right: 'medium', bottom: 'none' }}
                transitionEffect="none"
            />
        </label>

        {#if errorMessage && invalid}
            <div
                class="fun-ml-4 fun-mt-2 fun-flex fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600"
            >
                {errorMessage}
            </div>
        {/if}
    </div>
</div>
